import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { helpers } from "@simplybenefits/sb-utils";
import UsageBlock from './UsageBlock.js';
import Tooltip from '@mui/material/Tooltip';
import { Info as InfoIcon } from '@mui/icons-material';

/**
 * @param usage
 * @param coverage
 * @returns {*}
 * @constructor
 */
const HealthUsageBlock = ({ usage, coverage, fillColor, waivedEHC }) => {
    const { isCombinedMaximum } = coverage;

    return (
        <React.Fragment>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <div>
                    <Text
                        medium
                        bold
                        customStyle={{ 
                            flex: 1,
                            display: 'inline',
                            margin: '0 0 10px 0'
                        }}
                    >
                        { i18n.t(tokens.titles.majorMedical) }
                    </Text>
                    {
                        waivedEHC &&
                        <Chip
                            size="small"
                            label="Waived"
                            color="primary"
                            variant="outlined"
                            style={{
                                margin: "5px 10px",
                                borderRadius: 30,
                                borderWidth: 2,
                                borderColor: Theme.colors.lightGrey,
                                fontFamily: Theme.fonts.type.heading,
                                fontSize: 14,
                                color: '#666'
                            }}
                        />
                    }
                </div>

                {
                    isCombinedMaximum ?
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Tooltip 
                                    title={ i18n.t(tokens.labels.coverage.majorMedicalServicesSpendAmount, { amount: helpers.numberWithCommasDecimals(coverage.combinedMaximum - usage.totalUsed) })}
                                    arrow 
                                    placement="top"
                                >
                                    <InfoIcon aria-label="info" style={{ fontSize: 15, color: Theme.colors.mediumGrey, marginRight: 10 }} />
                                </Tooltip>

                                <Text small bold>
                                    { i18n.t(tokens.labels.coverage.combinedMaximumCombinedmax, { combinedMax: helpers.numberWithCommasDecimals(coverage.combinedMaximum) }) }
                                </Text>
                            </div>

                            <Text small bold margin="0 0 10px 0" align="right">
                                { i18n.t(tokens.labels.amountRemaining, { amount: helpers.numberWithCommasDecimals(coverage.combinedMaximum - usage.totalUsed) })}
                            </Text>
                        </div>
                        :
                        null
                }
                
            </div>

            {
                usage.used.map((item) => {
                    let limit = coverage[item.coverageColumn];
                    let limitText = false;
                    let extraText = '';
                    let notDollar = false;
                    let modifiedUsed = false; // for passing in a different used variable

                    if (item.coverageColumn === 'rc') {
                        limit = 50000;
                        limitText = i18n.t(tokens.labels.claims.referToYourBooklet);
                    }

                    if (item.type === "Convalescent Hospital") {
                        notDollar = true;
                        limitText = i18n.t(tokens.labels.coverage.daysLimit, { limit });
                    }

                    if (item.type === "Orthopedic Shoes" && coverage?.orthopedicShoesMaximumType?.toUpperCase() === 'PAIRS') {
                        notDollar = true;
                        limitText = i18n.t(tokens.labels.coverage.pairsLimit, { limit });
                    }

                    // 1 per 60 months (don't display dollar amount)
                    if (item.type === "Apnea Machine (CPAP)") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 ${ i18n.t(tokens.labels.coverage.unit) }`;
                        modifiedUsed = 1; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // 1 per lifetime
                    if (item.type === "Artificial Eye/Limb") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 ${ i18n.t(tokens.labels.coverage.unit) }`;
                        modifiedUsed = 1; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // When coverage is combined maximum and usage items are disabled but have usage prior to disabling 
                    if (isCombinedMaximum && Number(coverage[item.coverageColumn]) === 0 && item.used > 0) {
                        limit = modifiedUsed ? modifiedUsed : item.used;

                        if (item.type === "Convalescent Hospital") {
                            limitText = limit;
                        }
                    }

                    if (limit || limitText) {
                        return (
                            <UsageBlock
                                key={ item.type }
                                usage={{
                                    type: item.type,
                                    limit: limit,
                                    used: modifiedUsed ? modifiedUsed : item.used
                                }}
                                notDollar={ notDollar }
                                extraText={ extraText }
                                fillColor={ fillColor }
                                limitText={ limitText }
                                showRemaining={ !limitText }
                            />
                        );
                    }

                    return null;
                })
            }

            {
                usage.unused.map((item) => {
                    let limit = coverage[item.coverageColumn];
                    let limitText = false;

                    let extraText = '';
                    let notDollar = false;
                    let modifiedUsed = false; // for passing in a different used variable

                    if (item.coverageColumn === 'rc') {
                        limit = 50000;
                        limitText = i18n.t(tokens.labels.claims.referToYourBooklet);
                    }

                    if (item.type === "Convalescent Hospital") {
                        notDollar = true;
                        limitText = i18n.t(tokens.labels.coverage.daysLimit, { limit });
                    }

                    // 1 per 60 months (don't display dollar amount)
                    if (item.type === "Apnea Machine (CPAP)") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 ${ i18n.t(tokens.labels.coverage.unit) }`;
                        modifiedUsed = 0; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    // 1 per lifetime
                    if (item.type === "Artificial Eye/Limb") {
                        limit = 1;
                        notDollar = true;
                        limitText = `1 ${ i18n.t(tokens.labels.coverage.unit) }`;
                        modifiedUsed = 0; // Instead of a dollar amount, we want to show if it's been used once or not
                    }

                    if (coverage[item.coverageColumn] || limitText) {
                        return (
                            <UsageBlock
                                key={ item.type }
                                usage={{
                                    type: item.type,
                                    limit: limit,
                                    used: modifiedUsed ? modifiedUsed : item.used
                                }}
                                notDollar={ notDollar }
                                extraText={ extraText }
                                fillColor={ fillColor }
                                limitText={ limitText }
                            />
                        );
                    }

                    return null;
                })
            }
        </React.Fragment>
    ); 
};

export default HealthUsageBlock;